import { cloneDeep } from 'lodash';
import { getFramework } from '@/framework';
import { getLocaleStore } from '@/i18n/index';
import { isHttpUri } from '@/utils/file-kit';
import { getOrCreateScreenshotUrlCode, hydrateScreenshotUrlCode } from '@/utils/screenshot-url-kit';
import { uploadFileToObs } from '@/utils/upload-kit';
import type { ILayerProps } from '@/version-types/layer';
import { BrushType, type IBrushLayerProps } from '@/version-types/layer-props/brush-layer';

export async function uploadBrushFile(path: string, width: number, height: number) {
    const { $t } = getLocaleStore();
    const svgBlob = new Blob(
        [
            `<svg viewBox="0 0 ${width} ${height}"
    xmlns="http://www.w3.org/2000/svg"
    version="1.1"><path d="${path}"/></svg>`,
        ],
        { type: 'image/svg+xml' },
    );
    const { type } = svgBlob;
    const file = new File(
        [svgBlob],
        $t((v) => v.brushLayer.customBrush),
        { type },
    );
    return await uploadFileToObs(file);
}

export const getBrushScreenshotFingerprintData = (layer: ILayerProps) => {
    const data = cloneDeep(layer.data) as Partial<IBrushLayerProps>;
    delete data.screenshotUrl;
    delete data.url;
    delete data.__vipFuncType;
    delete data.__custom;
    delete data.__isQuickDraw;
    delete data.__drawPath;
    delete data.__isDrawPreview;

    return {
        width: Math.floor(layer.width),
        height: Math.floor(layer.height),
        ...data,
    };
};

export const hydrateBrushLayerScreenshotUrl = (layer: ILayerProps) => {
    const data = layer.data as IBrushLayerProps;
    data.screenshotUrl = hydrateScreenshotUrlCode({
        fingerprintData: getBrushScreenshotFingerprintData(layer),
        remoteUrl: data.screenshotUrl,
    });
    return layer;
};

export const initializeBrushLayerScreenshotUrl = (layer: ILayerProps) => {
    const data = layer.data as IBrushLayerProps;
    if (!layer.__runtime?.isInit && data.screenshotUrl && isHttpUri(data.screenshotUrl)) {
        hydrateBrushLayerScreenshotUrl(layer);
    }
    layer.__runtime = {
        ...layer.__runtime,
        isInit: true,
    };
    return layer;
};

export async function getScreenshotUrlCode(identity: string, layer: ILayerProps) {
    const snapshot = cloneDeep(layer);
    return getOrCreateScreenshotUrlCode({
        fingerprintData: getBrushScreenshotFingerprintData(snapshot),
        createDataUrl: async () => {
            const canvas = await getFramework(identity)
                .service.behavior.get(snapshot)
                ?.getLayerImageAsync?.(snapshot, false, true);
            return canvas?.toDataURL();
        },
    });
}

const timerMap = {};

export function updateScreenshotUrl(identity: string, id: string) {
    return new Promise((resolve) => {
        const layerTimer = timerMap[id];
        if (layerTimer) {
            clearTimeout(layerTimer);
        }
        (function (id) {
            timerMap[id] = setTimeout(() => {
                (async function (timerId) {
                    const framework = getFramework(identity);
                    let layer = framework.service.flatLayer.getLayer(id);
                    if (!framework.service.flatLayer.getLayer(id)) return;

                    const fingerprint = JSON.stringify(getBrushScreenshotFingerprintData(layer));
                    const code = await getScreenshotUrlCode(identity, layer);
                    layer = framework.service.flatLayer.getLayer(id);

                    if (layer) {
                        const data = layer.data as IBrushLayerProps;
                        if (fingerprint === JSON.stringify(getBrushScreenshotFingerprintData(layer))) {
                            data.screenshotUrl = code;
                        }

                        if (data.brushType === BrushType.Curve && !data.url && data.__drawPath) {
                            const res = await uploadBrushFile(data.__drawPath, data.naturalWidth, data.naturalHeight);
                            if (res.url) {
                                data.url = res.url;
                            }
                        }
                    }
                    if (timerId === timerMap[id]) {
                        delete timerMap[id];
                        resolve(layer);
                    }
                })(timerMap[id]);
            }, 33);
        })(id);
    });
}
